import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Users, Crown, Sparkles, Lock, MessageSquare, ArrowLeft, Shield, User as UserIcon, Trash2, FileText, Save,
  Coins, Zap, Clock, Plus, Minus, ShieldCheck, Ban, History, TrendingUp, Activity, Wand2, Circle, Loader2, Trophy,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "2-digit" });
  } catch {
    return "—";
  }
};

const GEN_CATEGORIES = [
  "Fantasy", "Sci-Fi", "Cyberpunk", "Anime", "Harem", "RPG", "Isekai", "Romance",
  "Adventure", "Mystery", "Superhero", "Villain", "Historical", "Horror", "Comedy",
  "Vampire", "Werewolf", "Mafia", "Yandere", "Tsundere", "Monster Girl", "Demon", "Angel",
  "Idol / VTuber", "Mecha", "Gaming", "Music", "Assistant / Helper", "Helpers",
  "Educational", "Slice of Life", "Paranormal",
];

const StatCard = ({ icon: Icon, label, value, accent, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
    className="rounded-2xl bg-[#131520] border border-white/5 p-5"
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${accent}`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <p className="font-display text-2xl font-extrabold text-white">{value?.toLocaleString("de-DE")}</p>
    <p className="text-xs text-slate-400 mt-0.5">{label}</p>
  </motion.div>
);

const RoleBadge = ({ role, isVip }) => {
  if (role === "owner")
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-400/30">OWNER</span>;
  if (role === "admin")
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-500/20 text-cyan-300 border border-cyan-400/30">ADMIN</span>;
  if (isVip)
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/30">VIP</span>;
  return <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/5 text-slate-400 border border-white/10">User</span>;
};

const CoinRow = ({ u, onGive, onHistory }) => {
  const [amt, setAmt] = useState("");
  const parsed = parseInt(amt, 10);
  const valid = Number.isFinite(parsed) && parsed > 0;
  return (
    <tr data-testid="owner-coin-row" className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
      <td className="px-4 py-3 text-white font-medium">
        <div className="flex items-center gap-2"><UserIcon className="w-4 h-4 text-slate-500" /> {u.name}</div>
      </td>
      <td className="px-4 py-3 text-slate-400">{u.username || u.email}</td>
      <td className="px-4 py-3 text-right">
        <span data-testid={`coin-balance-${u.id}`} className="inline-flex items-center gap-1 text-amber-300 font-semibold">
          <Coins className="w-3.5 h-3.5" /> {(u.coins ?? 0).toLocaleString("de-DE")}
        </span>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1.5 flex-wrap">
          {[100, 500, 1000].map((c) => (
            <button
              key={c}
              data-testid={`coin-quick-${c}-${u.id}`}
              onClick={() => onGive(u.id, c)}
              className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-all"
            >
              +{c}
            </button>
          ))}
          <input
            data-testid={`coin-input-${u.id}`}
            type="number"
            value={amt}
            onChange={(e) => setAmt(e.target.value)}
            placeholder="Zahl"
            className="w-20 bg-[#0d0e15] border border-white/10 focus:border-amber-400/50 rounded-lg px-2 py-1.5 text-white text-sm outline-none"
          />
          <button
            data-testid={`coin-give-${u.id}`}
            disabled={!valid}
            onClick={() => { onGive(u.id, parsed); setAmt(""); }}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 transition-all disabled:opacity-40"
            title="Geben"
          >
            <Plus className="w-3.5 h-3.5" /> Geben
          </button>
          <button
            data-testid={`coin-remove-${u.id}`}
            disabled={!valid}
            onClick={() => { onGive(u.id, -parsed); setAmt(""); }}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 transition-all disabled:opacity-40"
            title="Abziehen"
          >
            <Minus className="w-3.5 h-3.5" /> Abzug
          </button>
          <button
            data-testid={`coin-history-${u.id}`}
            onClick={() => onHistory(u)}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-white/5 text-slate-300 hover:bg-white/10 transition-all"
            title="Münzverlauf ansehen"
          >
            <History className="w-3.5 h-3.5" /> Verlauf
          </button>
        </div>
      </td>
    </tr>
  );
};

const SOON_MS = 3 * 24 * 60 * 60 * 1000;
const expiringSoon = (u) => {
  const now = Date.now();
  const within = (iso) => {
    if (!iso) return false;
    const t = new Date(iso).getTime() - now;
    return t > 0 && t <= SOON_MS;
  };
  const unl = u.perk_unlimited && within(u.perk_unlimited_until);
  const vip = u.role === "user" && u.is_vip && within(u.vip_until);
  return unl || vip;
};

const RightsRow = ({ u, onSetUnlimited, onToggleAdult, onToggleAdmin, soon }) => {
  const active = u.perk_unlimited && (!u.perk_unlimited_until || new Date(u.perk_unlimited_until) > new Date());
  const status = !active
    ? "Aus"
    : u.perk_unlimited_until
      ? `Bis ${fmtDate(u.perk_unlimited_until)}`
      : "Dauerhaft";
  return (
    <tr data-testid="owner-rights-row" className={`border-b border-white/5 last:border-0 hover:bg-white/[0.02] align-top ${soon ? "bg-amber-500/[0.07]" : ""}`}>
      <td className="px-4 py-3 text-white font-medium">
        <div className="flex items-center gap-2"><UserIcon className="w-4 h-4 text-slate-500" /> {u.name}</div>
        <div className="text-xs text-slate-500 mt-0.5">{u.username || u.email}</div>
        <div className="mt-1 flex items-center gap-1.5">
          <RoleBadge role={u.role} isVip={u.is_vip} />
          {soon && (
            <span data-testid={`rights-soon-badge-${u.id}`} className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-400/30">
              Läuft bald ab
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-4 h-4 text-cyan-400" />
          <span className="text-sm text-white">Unbegrenzte Nachrichten</span>
          <span data-testid={`rights-unlimited-status-${u.id}`} className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${active ? "bg-cyan-500/20 text-cyan-300" : "bg-white/5 text-slate-400"}`}>{status}</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              data-testid={`rights-unlimited-${d}-${u.id}`}
              onClick={() => onSetUnlimited(u.id, { enabled: true, days: d })}
              className="px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-white/5 text-slate-300 hover:bg-cyan-500/20 hover:text-cyan-300 transition-all"
            >
              {d} Tage
            </button>
          ))}
          <button
            data-testid={`rights-unlimited-perm-${u.id}`}
            onClick={() => onSetUnlimited(u.id, { enabled: true, days: 0 })}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25 transition-all"
          >
            <Clock className="w-3.5 h-3.5" /> Dauerhaft
          </button>
          <button
            data-testid={`rights-unlimited-off-${u.id}`}
            onClick={() => onSetUnlimited(u.id, { enabled: false, days: 0 })}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-white/5 text-slate-400 hover:bg-rose-500/15 hover:text-rose-300 transition-all"
          >
            <Ban className="w-3.5 h-3.5" /> Aus
          </button>
        </div>
      </td>
      <td className="px-4 py-3 text-center">
        <button
          data-testid={`rights-adult-${u.id}`}
          onClick={() => onToggleAdult(u)}
          className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${u.perk_adult ? "bg-fuchsia-500/20 text-fuchsia-300 hover:bg-fuchsia-500/30" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}
        >
          {u.perk_adult ? "18+ an" : "18+ aus"}
        </button>
      </td>
      <td className="px-4 py-3 text-center">
        <button
          data-testid={`rights-admin-${u.id}`}
          onClick={() => onToggleAdmin(u)}
          className={`flex items-center gap-1 mx-auto px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${u.role === "admin" ? "bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30" : "bg-white/5 text-slate-400 hover:bg-white/10"}`}
        >
          <ShieldCheck className="w-3.5 h-3.5" /> {u.role === "admin" ? "Admin an" : "Admin aus"}
        </button>
      </td>
    </tr>
  );
};

export default function OwnerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [policy, setPolicy] = useState("");
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [grantTarget, setGrantTarget] = useState(null);
  const [grantDays, setGrantDays] = useState(7);
  const [granting, setGranting] = useState(false);
  const [permUnlimited, setPermUnlimited] = useState(false);
  const [permAdult, setPermAdult] = useState(false);
  const [roleAdmin, setRoleAdmin] = useState(false);
  const [coinsAmount, setCoinsAmount] = useState(0);
  const [historyUser, setHistoryUser] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [soonOnly, setSoonOnly] = useState(false);
  const [chats, setChats] = useState([]);
  const [activity, setActivity] = useState([]);
  const [chatView, setChatView] = useState(null);
  const [chatViewLoading, setChatViewLoading] = useState(false);
  const [genCategory, setGenCategory] = useState("");
  const [genGender, setGenGender] = useState("");
  const [generating, setGenerating] = useState(false);
  const [genResults, setGenResults] = useState([]);
  const [animeBusyId, setAnimeBusyId] = useState(null);
  const [chatSearch, setChatSearch] = useState("");
  const [bulkJob, setBulkJob] = useState(null);
  const [weeklyRunning, setWeeklyRunning] = useState(false);

  const pollBulk = async () => {
    try {
      const r = await api.get("/owner/characters/convert-all-anime/status");
      setBulkJob(r.data);
      if (r.data.running) {
        setTimeout(pollBulk, 3000);
      } else {
        const cr = await api.get("/owner/characters");
        setCharacters(cr.data);
        if (r.data.total) toast.success(`Fertig: ${(r.data.done || 0) - (r.data.failed || 0)}/${r.data.total} umgewandelt`);
      }
    } catch (_) {}
  };

  const startBulkAnime = async () => {
    if (bulkJob?.running) return;
    if (!window.confirm("Alle Anime-Charaktere in Anime-Stil umwandeln? Das erzeugt viele KI-Bilder und dauert einige Minuten.")) return;
    try {
      const r = await api.post("/owner/characters/convert-all-anime");
      if (r.data.started === false) {
        toast.info(r.data.message || "Bereits in Arbeit");
      } else {
        toast.success(`Umwandlung gestartet (${r.data.total} Charaktere)`);
      }
      setBulkJob({ running: true, total: r.data.total || 0, done: 0, failed: 0 });
      pollBulk();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Start fehlgeschlagen");
    }
  };

  const animeAvatar = async (c) => {
    setAnimeBusyId(c.id);
    try {
      const r = await api.post(`/owner/characters/${c.id}/anime-avatar`);
      setCharacters((list) => list.map((x) => (x.id === c.id ? { ...x, avatar_url: r.data.avatar_url } : x)));
      toast.success(`Anime-Bild für „${c.name}" erstellt`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Anime-Bild fehlgeschlagen");
    } finally {
      setAnimeBusyId(null);
    }
  };

  useEffect(() => {
    if (user && !user.is_owner) {
      navigate("/");
      return;
    }
    Promise.all([
      api.get("/owner/stats").then((r) => setStats(r.data)),
      api.get("/owner/users").then((r) => setUsers(r.data)),
      api.get("/owner/characters").then((r) => setCharacters(r.data)),
      api.get("/owner/settings").then((r) => setPolicy(r.data.rp_guidelines || "")),
      api.get("/owner/chats").then((r) => setChats(r.data)).catch(() => {}),
      api.get("/owner/activity").then((r) => setActivity(r.data)).catch(() => {}),
      api.get("/owner/characters/convert-all-anime/status").then((r) => { setBulkJob(r.data); if (r.data.running) pollBulk(); }).catch(() => {}),
    ]).catch(() => navigate("/"));
  }, [user, navigate]);

  const openChatTranscript = async (sessionId) => {
    setChatView({ loading: true });
    setChatViewLoading(true);
    try {
      const r = await api.get(`/owner/chats/${sessionId}/messages`);
      setChatView(r.data);
    } catch (e) {
      toast.error("Chat konnte nicht geladen werden");
      setChatView(null);
    } finally {
      setChatViewLoading(false);
    }
  };

  const generateCharacter = async () => {
    setGenerating(true);
    try {
      const body = {};
      if (genCategory) body.category = genCategory;
      if (genGender) body.gender = genGender;
      const r = await api.post("/owner/generate-character", body);
      setGenResults((list) => [r.data, ...list]);
      setCharacters((list) => [r.data, ...list]);
      toast.success(`Charakter „${r.data.name}" generiert!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generierung fehlgeschlagen");
    } finally {
      setGenerating(false);
    }
  };

  const runWeeklyReward = async () => {
    setWeeklyRunning(true);
    try {
      const r = await api.post("/owner/weekly-reward/run");
      if (r.data.skipped) {
        toast.info("Übersprungen: " + (r.data.reason || ""));
      } else {
        toast.success(`Champion: ${r.data.champion} · ${r.data.coins_awarded} Münzen vergeben`);
        const cr = await api.get("/owner/characters");
        setCharacters(cr.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    } finally {
      setWeeklyRunning(false);
    }
  };

  const refreshActivity = async () => {
    try {
      const r = await api.get("/owner/activity");
      setActivity(r.data);
    } catch (_) {}
  };

  const fmtAgo = (sec) => {
    if (sec === null || sec === undefined) return "Nie";
    if (sec < 60) return "gerade eben";
    if (sec < 3600) return `vor ${Math.floor(sec / 60)} Min`;
    if (sec < 86400) return `vor ${Math.floor(sec / 3600)} Std`;
    return `vor ${Math.floor(sec / 86400)} Tagen`;
  };

  const openGrant = (u) => {
    setGrantTarget(u);
    setGrantDays(7);
    setPermUnlimited(!!u.perk_unlimited);
    setPermAdult(!!u.perk_adult);
    setRoleAdmin(u.role === "admin");
    setCoinsAmount(0);
  };

  const saveGrant = async () => {
    if (!grantTarget) return;
    setGranting(true);
    try {
      await api.post(`/owner/users/${grantTarget.id}/perks`, {
        perk_unlimited: permUnlimited,
        perk_adult: permAdult,
        role: roleAdmin ? "admin" : "user",
      });
      if (grantDays > 0) {
        await api.post(`/owner/users/${grantTarget.id}/vip`, { days: grantDays });
      }
      if (coinsAmount && coinsAmount !== 0) {
        await api.post(`/owner/users/${grantTarget.id}/coins`, { amount: coinsAmount });
      }
      const r = await api.get("/owner/users");
      setUsers(r.data);
      toast.success("Freigaben gespeichert");
      setGrantTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally {
      setGranting(false);
    }
  };

  const revokeVip = async () => {
    if (!grantTarget) return;
    setGranting(true);
    try {
      await api.post(`/owner/users/${grantTarget.id}/vip`, { days: 0 });
      const r = await api.get("/owner/users");
      setUsers(r.data);
      toast.success("VIP entfernt");
      setGrantTarget(null);
    } catch (e) {
      toast.error("Aktion fehlgeschlagen");
    } finally {
      setGranting(false);
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Nutzer "${u.name}" (${u.email || u.username}) endgültig löschen? Alle Daten & Charaktere gehen verloren.`)) return;
    try {
      await api.delete(`/owner/users/${u.id}`);
      setUsers((list) => list.filter((x) => x.id !== u.id));
      toast.success("Nutzer gelöscht");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const refetchUsers = async () => {
    const r = await api.get("/owner/users");
    setUsers(r.data);
  };

  const giveCoins = async (userId, amount) => {
    if (!amount) return;
    try {
      await api.post(`/owner/users/${userId}/coins`, { amount });
      await refetchUsers();
      toast.success(amount > 0 ? `+${amount} Münzen vergeben` : `${Math.abs(amount)} Münzen abgezogen`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  };

  const setUnlimited = async (userId, { enabled, days }) => {
    try {
      await api.post(`/owner/users/${userId}/perks`, { perk_unlimited: enabled, unlimited_days: days || 0 });
      await refetchUsers();
      toast.success(enabled ? (days ? `Unbegrenzt für ${days} Tage` : "Dauerhaft unbegrenzt") : "Unbegrenzt entfernt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  };

  const toggleAdult = async (u) => {
    try {
      await api.post(`/owner/users/${u.id}/perks`, { perk_adult: !u.perk_adult });
      await refetchUsers();
      toast.success(!u.perk_adult ? "18+ freigeschaltet" : "18+ entfernt");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  };

  const toggleAdmin = async (u) => {
    try {
      await api.post(`/owner/users/${u.id}/perks`, { role: u.role === "admin" ? "user" : "admin" });
      await refetchUsers();
      toast.success(u.role === "admin" ? "Admin-Rechte entzogen" : "Admin-Rechte vergeben");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    }
  };

  const openHistory = async (u) => {
    setHistoryUser(u);
    setHistoryRows([]);
    setHistoryLoading(true);
    try {
      const r = await api.get(`/owner/users/${u.id}/coins/history`);
      setHistoryRows(r.data.history || []);
    } catch (e) {
      toast.error("Verlauf konnte nicht geladen werden");
    } finally {
      setHistoryLoading(false);
    }
  };

  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      await api.put("/owner/settings", { rp_guidelines: policy });
      toast.success("Richtlinien gespeichert");
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSavingPolicy(false);
    }
  };

  const toggleVip = async (c) => {
    try {
      await api.put(`/characters/${c.id}`, { is_vip: !c.is_vip });
      setCharacters((list) => list.map((x) => (x.id === c.id ? { ...x, is_vip: !x.is_vip } : x)));
      toast.success(!c.is_vip ? `${c.name} ist jetzt VIP` : `${c.name} ist kein VIP mehr`);
    } catch (e) {
      toast.error("Aktion fehlgeschlagen");
    }
  };

  const deleteChar = async (c) => {
    if (!window.confirm(`${c.name} endgültig löschen? Alle Chats gehen verloren.`)) return;
    try {
      await api.delete(`/characters/${c.id}`);
      setCharacters((list) => list.filter((x) => x.id !== c.id));
      toast.success(`${c.name} gelöscht`);
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const STATS = stats
    ? [
        { icon: Users, label: "Nutzer", value: stats.total_users, accent: "bg-gradient-to-br from-violet-600 to-indigo-600" },
        { icon: Crown, label: "VIP Mitglieder", value: stats.vip_users, accent: "bg-gradient-to-br from-amber-500 to-fuchsia-600" },
        { icon: Sparkles, label: "Charaktere", value: stats.total_characters, accent: "bg-gradient-to-br from-fuchsia-600 to-pink-600" },
        { icon: Crown, label: "VIP Charaktere", value: stats.vip_characters, accent: "bg-gradient-to-br from-amber-500 to-orange-600" },
        { icon: Lock, label: "Private Charaktere", value: stats.private_characters, accent: "bg-gradient-to-br from-slate-600 to-slate-700" },
        { icon: MessageSquare, label: "Nachrichten", value: stats.total_messages, accent: "bg-gradient-to-br from-cyan-600 to-blue-600" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-[#090A0F] grid-bg">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <button
          data-testid="owner-back"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-6 transition-all"
        >
          <ArrowLeft className="w-4 h-4" /> Zurück
        </button>

        <div className="flex items-center gap-2 mb-8">
          <Shield className="w-6 h-6 text-amber-300" />
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">Owner Dashboard</h1>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          {STATS.map((s, i) => (
            <StatCard key={s.label} {...s} index={i} />
          ))}
        </div>

        <Tabs defaultValue="users" className="w-full">
          <TabsList className="bg-[#131520] border border-white/5 flex-wrap h-auto">
            <TabsTrigger value="users" data-testid="owner-tab-users" className="data-[state=active]:bg-white/10">
              <Users className="w-4 h-4 mr-2" /> Nutzer ({users.length})
            </TabsTrigger>
            <TabsTrigger value="characters" data-testid="owner-tab-characters" className="data-[state=active]:bg-white/10">
              <Sparkles className="w-4 h-4 mr-2" /> Charaktere ({characters.length})
            </TabsTrigger>
            <TabsTrigger value="coins" data-testid="owner-tab-coins" className="data-[state=active]:bg-white/10">
              <Coins className="w-4 h-4 mr-2" /> Münzen
            </TabsTrigger>
            <TabsTrigger value="rights" data-testid="owner-tab-rights" className="data-[state=active]:bg-white/10">
              <Zap className="w-4 h-4 mr-2" /> Rechte
            </TabsTrigger>
            <TabsTrigger value="policy" data-testid="owner-tab-policy" className="data-[state=active]:bg-white/10">
              <FileText className="w-4 h-4 mr-2" /> Richtlinien
            </TabsTrigger>
            <TabsTrigger value="chats" data-testid="owner-tab-chats" className="data-[state=active]:bg-white/10">
              <MessageSquare className="w-4 h-4 mr-2" /> Chats
            </TabsTrigger>
            <TabsTrigger value="activity" data-testid="owner-tab-activity" className="data-[state=active]:bg-white/10">
              <Activity className="w-4 h-4 mr-2" /> Aktivität
            </TabsTrigger>
            <TabsTrigger value="generate" data-testid="owner-tab-generate" className="data-[state=active]:bg-white/10">
              <Wand2 className="w-4 h-4 mr-2" /> Generieren
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="mt-5">
            <div className="rounded-2xl bg-[#131520] border border-white/5 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-white/5">
                      <th className="px-4 py-3 font-semibold">Name</th>
                      <th className="px-4 py-3 font-semibold">E-Mail / User</th>
                      <th className="px-4 py-3 font-semibold">Rolle</th>
                      <th className="px-4 py-3 font-semibold">VIP bis</th>
                      <th className="px-4 py-3 font-semibold">Login</th>
                      <th className="px-4 py-3 font-semibold text-right">Nachr. heute</th>
                      <th className="px-4 py-3 font-semibold text-right">Münzen</th>
                      <th className="px-4 py-3 font-semibold text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} data-testid={`owner-user-row`} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-white font-medium flex items-center gap-2">
                          <UserIcon className="w-4 h-4 text-slate-500" /> {u.name}
                        </td>
                        <td className="px-4 py-3 text-slate-400">{u.username || u.email}</td>
                        <td className="px-4 py-3"><RoleBadge role={u.role} isVip={u.is_vip} /></td>
                        <td className="px-4 py-3 text-slate-400">{u.is_vip && u.role === "user" ? fmtDate(u.vip_until) : "—"}</td>
                        <td className="px-4 py-3 text-slate-500 capitalize">{u.auth_provider}</td>
                        <td className="px-4 py-3 text-right text-slate-300">{u.messages_today?.toLocaleString("de-DE")}</td>
                        <td className="px-4 py-3 text-right text-amber-300 font-medium">{(u.coins ?? 0).toLocaleString("de-DE")}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            {u.role !== "owner" && (
                              <>
                                <button
                                  data-testid={`owner-grant-vip-${u.id}`}
                                  onClick={() => openGrant(u)}
                                  title="VIP-Zugang vergeben"
                                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-white/5 text-slate-300 hover:bg-fuchsia-500/20 hover:text-fuchsia-300 transition-all"
                                >
                                  <Crown className="w-3.5 h-3.5" /> VIP
                                </button>
                                <button
                                  data-testid={`owner-delete-user-${u.id}`}
                                  onClick={() => deleteUser(u)}
                                  title="Nutzer löschen"
                                  className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-all"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="characters" className="mt-5">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <p className="text-sm text-slate-400">
                {bulkJob?.running
                  ? `Wandelt um... ${bulkJob.done || 0}/${bulkJob.total || 0}${bulkJob.failed ? ` (${bulkJob.failed} fehlgeschlagen)` : ""}`
                  : "Wandle alle Anime-Charaktere mit einem Klick in KI-Anime-Bilder um."}
              </p>
              <button
                data-testid="owner-bulk-anime-btn"
                onClick={startBulkAnime}
                disabled={bulkJob?.running}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-lg shadow-violet-500/20 active:scale-95 transition-all disabled:opacity-60"
              >
                {bulkJob?.running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                {bulkJob?.running ? "Läuft..." : "Alle Anime umwandeln"}
              </button>
            </div>
            {bulkJob?.running && (
              <div className="h-1.5 w-full rounded-full bg-white/5 overflow-hidden mb-4">
                <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all" style={{ width: `${bulkJob.total ? Math.round((bulkJob.done / bulkJob.total) * 100) : 0}%` }} />
              </div>
            )}
            <div className="rounded-2xl bg-[#131520] border border-white/5 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-white/5">
                      <th className="px-4 py-3 font-semibold">Charakter</th>
                      <th className="px-4 py-3 font-semibold">Kategorie</th>
                      <th className="px-4 py-3 font-semibold">Ersteller</th>
                      <th className="px-4 py-3 font-semibold">Typ</th>
                      <th className="px-4 py-3 font-semibold text-right">Chats</th>
                      <th className="px-4 py-3 font-semibold text-right">Aktionen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {characters.map((c) => (
                      <tr key={c.id} data-testid="owner-char-row" className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                        <td className="px-4 py-3 text-white font-medium flex items-center gap-3">
                          <img
                            src={c.avatar_url || "https://images.unsplash.com/photo-1559828801-04565cd31e27?crop=entropy&cs=srgb&fm=jpg&q=85&w=200"}
                            alt={c.name}
                            className="w-8 h-8 rounded-lg object-cover border border-white/10"
                          />
                          {c.name}
                        </td>
                        <td className="px-4 py-3 text-slate-400">{c.category}</td>
                        <td className="px-4 py-3 text-slate-400">{c.creator_name}</td>
                        <td className="px-4 py-3">
                          <div className="flex gap-1.5">
                            {c.is_vip && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-fuchsia-500/20 text-fuchsia-300">VIP</span>}
                            {c.is_public === false && <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-500/20 text-slate-300">Privat</span>}
                            {!c.is_vip && c.is_public !== false && <span className="text-slate-500 text-xs">Öffentlich</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right text-slate-300">{c.chat_count?.toLocaleString("de-DE")}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              data-testid={`owner-anime-avatar-${c.id}`}
                              onClick={() => animeAvatar(c)}
                              disabled={animeBusyId === c.id}
                              title="Avatar in Anime-Stil umwandeln"
                              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold bg-violet-500/15 text-violet-300 hover:bg-violet-500/25 transition-all disabled:opacity-60"
                            >
                              {animeBusyId === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                              Anime
                            </button>
                            <button
                              data-testid={`owner-toggle-vip-${c.id}`}
                              onClick={() => toggleVip(c)}
                              title={c.is_vip ? "VIP entfernen" : "Zu VIP machen"}
                              className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
                                c.is_vip
                                  ? "bg-fuchsia-500/20 text-fuchsia-300 hover:bg-fuchsia-500/30"
                                  : "bg-white/5 text-slate-400 hover:bg-white/10 hover:text-amber-300"
                              }`}
                            >
                              <Crown className="w-3.5 h-3.5" /> {c.is_vip ? "VIP an" : "VIP aus"}
                            </button>
                            <button
                              data-testid={`owner-delete-char-${c.id}`}
                              onClick={() => deleteChar(c)}
                              title="Löschen"
                              className="p-1.5 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="coins" className="mt-5">
            <div className="rounded-2xl bg-[#131520] border border-white/5 overflow-hidden">
              <div className="px-4 pt-4 pb-2 flex items-center gap-2">
                <Coins className="w-5 h-5 text-amber-300" />
                <p className="text-sm text-slate-300">Münzen an Nutzer vergeben oder abziehen.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-white/5">
                      <th className="px-4 py-3 font-semibold">Name</th>
                      <th className="px-4 py-3 font-semibold">E-Mail / User</th>
                      <th className="px-4 py-3 font-semibold text-right">Guthaben</th>
                      <th className="px-4 py-3 font-semibold text-right">Münzen geben</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.filter((u) => u.role !== "owner").map((u) => (
                      <CoinRow key={u.id} u={u} onGive={giveCoins} onHistory={openHistory} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="rights" className="mt-5">
            {(() => {
              const nonOwner = users.filter((u) => u.role !== "owner");
              const unlimitedCount = nonOwner.filter((u) => u.perk_unlimited && (!u.perk_unlimited_until || new Date(u.perk_unlimited_until) > new Date())).length;
              const adminCount = nonOwner.filter((u) => u.role === "admin").length;
              const adultCount = nonOwner.filter((u) => u.perk_adult).length;
              const cards = [
                { icon: Zap, label: "Unbegrenzt", value: unlimitedCount, accent: "text-cyan-300 bg-cyan-500/15" },
                { icon: ShieldCheck, label: "Admins", value: adminCount, accent: "text-cyan-300 bg-cyan-500/15" },
                { icon: TrendingUp, label: "18+ freigeschaltet", value: adultCount, accent: "text-fuchsia-300 bg-fuchsia-500/15" },
              ];
              return (
                <div className="grid grid-cols-3 gap-3 mb-4" data-testid="rights-summary">
                  {cards.map((c) => (
                    <div key={c.label} className="rounded-2xl bg-[#131520] border border-white/5 p-4 flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${c.accent}`}>
                        <c.icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p data-testid={`rights-summary-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="font-display text-xl font-extrabold text-white leading-none">{c.value}</p>
                        <p className="text-xs text-slate-400 mt-1">{c.label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
            {(() => {
              const soonCount = users.filter((u) => u.role !== "owner" && expiringSoon(u)).length;
              return (
                <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                  <p className="text-xs text-slate-500">
                    {soonCount > 0 ? `${soonCount} Nutzer mit Freigaben, die bald ablaufen` : "Keine Freigaben, die bald ablaufen"}
                  </p>
                  <button
                    data-testid="rights-filter-soon"
                    onClick={() => setSoonOnly((v) => !v)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                      soonOnly
                        ? "bg-amber-500/20 text-amber-300 border-amber-400/40"
                        : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    <Clock className="w-3.5 h-3.5" /> Läuft bald ab {soonOnly ? "(an)" : ""}
                  </button>
                </div>
              );
            })()}
            <div className="rounded-2xl bg-[#131520] border border-white/5 overflow-hidden">
              <div className="px-4 pt-4 pb-2 flex items-center gap-2">
                <Zap className="w-5 h-5 text-cyan-300" />
                <p className="text-sm text-slate-300">Rechte vergeben: unbegrenzte Nachrichten (befristet oder dauerhaft), 18+ Inhalte und Admin-Rechte.</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-white/5">
                      <th className="px-4 py-3 font-semibold">Nutzer</th>
                      <th className="px-4 py-3 font-semibold">Unbegrenzte Nachrichten</th>
                      <th className="px-4 py-3 font-semibold text-center">18+</th>
                      <th className="px-4 py-3 font-semibold text-center">Admin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.filter((u) => u.role !== "owner").filter((u) => !soonOnly || expiringSoon(u)).map((u) => (
                      <RightsRow
                        key={u.id}
                        u={u}
                        soon={expiringSoon(u)}
                        onSetUnlimited={setUnlimited}
                        onToggleAdult={toggleAdult}
                        onToggleAdmin={toggleAdmin}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="policy" className="mt-5">
            <div className="rounded-2xl bg-[#131520] border border-white/5 p-6">
              <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-fuchsia-400" /> RP-Richtlinien / Regeln
              </h3>
              <p className="text-sm text-slate-400 mt-1 mb-4">
                Diese Regeln werden jedem Charakter-Prompt hinzugefügt und steuern, wie die KI antwortet.
                Als Owner kannst du sie frei anpassen.
              </p>
              <textarea
                data-testid="owner-policy-textarea"
                rows={10}
                value={policy}
                onChange={(e) => setPolicy(e.target.value)}
                className="w-full bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 outline-none transition-all resize-none text-sm leading-relaxed"
              />
              <button
                data-testid="owner-policy-save"
                onClick={savePolicy}
                disabled={savingPolicy}
                className="mt-4 flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold rounded-xl px-6 py-3 shadow-lg shadow-violet-500/20 active:scale-95 transition-all disabled:opacity-60"
              >
                <Save className="w-4 h-4" /> {savingPolicy ? "Speichern..." : "Richtlinien speichern"}
              </button>
            </div>
          </TabsContent>

          <TabsContent value="chats" className="mt-5">
            <div className="rounded-2xl bg-[#131520] border border-white/5 overflow-hidden">
              <div className="px-4 pt-4 pb-2 flex items-center gap-2 flex-wrap">
                <MessageSquare className="w-5 h-5 text-fuchsia-400" />
                <p className="text-sm text-slate-300">Unterhaltungen der Nutzer einsehen ({chats.length}).</p>
                <input
                  data-testid="owner-chat-search"
                  value={chatSearch}
                  onChange={(e) => setChatSearch(e.target.value)}
                  placeholder="Nach Nutzer oder Charakter suchen..."
                  className="ml-auto w-full sm:w-64 bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-lg px-3 py-1.5 text-white text-sm outline-none"
                />
              </div>
              <div className="divide-y divide-white/5">
                {(() => {
                  const q = chatSearch.trim().toLowerCase();
                  const filtered = q
                    ? chats.filter((c) => (c.user_name || "").toLowerCase().includes(q) || (c.character_name || "").toLowerCase().includes(q))
                    : chats;
                  if (filtered.length === 0) {
                    return <p className="px-4 py-10 text-center text-sm text-slate-500" data-testid="owner-chats-empty">{chats.length === 0 ? "Noch keine Chats vorhanden." : "Keine Treffer."}</p>;
                  }
                  return filtered.map((c) => (
                    <div key={c.session_id} data-testid="owner-chat-row" className="flex items-center gap-3 px-4 py-3 hover:bg-white/[0.02]">
                      <img src={c.character_avatar || "https://via.placeholder.com/40"} alt="" className="w-10 h-10 rounded-lg object-cover object-top flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-white truncate">
                          <span className="font-semibold">{c.user_name}</span>
                          <span className="text-slate-500"> &#8596; </span>
                          {c.character_name}
                        </p>
                        <p className="text-xs text-slate-500">{c.message_count} Nachrichten &middot; {fmtDate(c.updated_at)}</p>
                      </div>
                      <button
                        data-testid={`owner-chat-view-${c.session_id}`}
                        onClick={() => openChatTranscript(c.session_id)}
                        className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold bg-fuchsia-500/15 text-fuchsia-300 hover:bg-fuchsia-500/25 transition-all"
                      >
                        Ansehen
                      </button>
                    </div>
                  ));
                })()}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="activity" className="mt-5">
            <div className="rounded-2xl bg-[#131520] border border-white/5 overflow-hidden">
              <div className="px-4 pt-4 pb-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-emerald-400" />
                  <p className="text-sm text-slate-300">
                    {activity.filter((a) => a.online).length} online &middot; {activity.length} Nutzer gesamt
                  </p>
                </div>
                <button
                  data-testid="owner-activity-refresh"
                  onClick={refreshActivity}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/5 text-slate-300 hover:bg-white/10 transition-all"
                >
                  Aktualisieren
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500 uppercase tracking-wider border-b border-white/5">
                      <th className="px-4 py-3 font-semibold">Status</th>
                      <th className="px-4 py-3 font-semibold">Nutzer</th>
                      <th className="px-4 py-3 font-semibold">Zuletzt online</th>
                      <th className="px-4 py-3 font-semibold">Registriert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.filter((a) => a.role !== "owner").map((a) => (
                      <tr key={a.id} data-testid="owner-activity-row" className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${a.online ? "text-emerald-400" : "text-slate-500"}`}>
                            <Circle className={`w-2 h-2 ${a.online ? "fill-emerald-400 text-emerald-400" : "fill-slate-600 text-slate-600"}`} />
                            {a.online ? "Online" : "Offline"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-white">
                          {a.name}
                          <span className="block text-xs text-slate-500">{a.username || a.email}</span>
                        </td>
                        <td className="px-4 py-3 text-slate-400">{fmtAgo(a.seconds_ago)}</td>
                        <td className="px-4 py-3 text-slate-500">{a.created_at ? fmtDate(a.created_at) : "\u2014"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="generate" className="mt-5">
            <div className="rounded-2xl bg-[#131520] border border-white/5 p-6">
              <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-fuchsia-400" /> Charakter per KI generieren
              </h3>
              <p className="text-sm text-slate-400 mt-1 mb-4">
                Erstellt mit einem Klick einen komplett neuen Charakter (Name, Pers&ouml;nlichkeit, Begr&uuml;&szlig;ung + KI-Bild). Leer lassen = zuf&auml;llig.
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Kategorie</label>
                  <select
                    data-testid="gen-category"
                    value={genCategory}
                    onChange={(e) => setGenCategory(e.target.value)}
                    className="bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl px-3 py-2.5 text-white text-sm outline-none"
                  >
                    <option value="">Zuf&auml;llig</option>
                    {GEN_CATEGORIES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Geschlecht</label>
                  <select
                    data-testid="gen-gender"
                    value={genGender}
                    onChange={(e) => setGenGender(e.target.value)}
                    className="bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl px-3 py-2.5 text-white text-sm outline-none"
                  >
                    <option value="">Zuf&auml;llig</option>
                    <option value="Female">Weiblich</option>
                    <option value="Male">M&auml;nnlich</option>
                    <option value="Non-binary">Divers</option>
                  </select>
                </div>
                <button
                  data-testid="gen-generate-btn"
                  onClick={generateCharacter}
                  disabled={generating}
                  className="flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold rounded-xl px-6 py-2.5 shadow-lg shadow-violet-500/20 active:scale-95 transition-all disabled:opacity-60"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                  {generating ? "Generiert..." : "Generieren"}
                </button>
              </div>

              {generating && (
                <p className="text-xs text-slate-500 mt-3">KI erstellt Charakter &amp; Bild &ndash; das dauert ein paar Sekunden...</p>
              )}

              {genResults.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Neu generiert ({genResults.length})</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="gen-results">
                    {genResults.map((c) => (
                      <div key={c.id} data-testid="gen-result-card" className="rounded-2xl bg-[#0d0e15] border border-white/10 overflow-hidden">
                        <div className="aspect-[4/5] overflow-hidden bg-white/5">
                          {c.avatar_url ? (
                            <img src={c.avatar_url} alt={c.name} className="w-full h-full object-cover object-top" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-600"><Sparkles className="w-8 h-8" /></div>
                          )}
                        </div>
                        <div className="p-3">
                          <p className="text-sm font-bold text-white truncate">{c.name}</p>
                          <p className="text-xs text-slate-400 truncate">{c.title}</p>
                          <span className="inline-block mt-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-fuchsia-500/15 text-fuchsia-300">{c.category}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-[#131520] border border-white/5 p-6 mt-5" data-testid="weekly-reward-card">
              <h3 className="font-display text-lg font-bold text-white flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-400" /> Wochen-Champion
              </h3>
              <p className="text-sm text-slate-400 mt-1 mb-4">
                Kürt den Top-Charakter der letzten 7 Tage: Der Ersteller erh&auml;lt <span className="text-amber-300 font-semibold">500 M&uuml;nzen</span> und der Charakter ein gl&auml;nzendes Abzeichen. L&auml;uft automatisch jeden Montag &ndash; hier manuell ausl&ouml;sen.
              </p>
              <button
                data-testid="weekly-reward-btn"
                onClick={runWeeklyReward}
                disabled={weeklyRunning}
                className="flex items-center gap-2 bg-gradient-to-r from-amber-500 to-fuchsia-600 text-white font-semibold rounded-xl px-6 py-2.5 shadow-lg shadow-amber-500/20 active:scale-95 transition-all disabled:opacity-60"
              >
                {weeklyRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
                {weeklyRunning ? "Küre..." : "Champion jetzt küren"}
              </button>
            </div>
          </TabsContent>

        </Tabs>

        <Dialog open={!!grantTarget} onOpenChange={(o) => !o && setGrantTarget(null)}>
          <DialogContent className="glass border-white/10 text-slate-200" data-testid="grant-vip-modal">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Crown className="w-5 h-5 text-amber-300" /> VIP vergeben
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Temporären VIP-Zugang für <span className="text-white font-semibold">{grantTarget?.name}</span> freischalten.
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-3 gap-2 my-2">
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  data-testid={`grant-quick-${d}`}
                  onClick={() => setGrantDays(d)}
                  className={`rounded-xl py-3 font-semibold text-sm border transition-all ${
                    grantDays === d
                      ? "bg-gradient-to-r from-amber-500 to-fuchsia-600 text-white border-transparent"
                      : "bg-white/5 text-slate-300 border-white/10 hover:bg-white/10"
                  }`}
                >
                  {d} Tage
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm text-slate-400 whitespace-nowrap">Eigene Anzahl:</label>
              <input
                data-testid="grant-custom-days"
                type="number"
                min="0"
                value={grantDays}
                onChange={(e) => setGrantDays(parseInt(e.target.value, 10) || 0)}
                className="flex-1 bg-[#0d0e15] border border-white/10 focus:border-fuchsia-500/50 rounded-xl px-3 py-2 text-white outline-none"
              />
              <span className="text-sm text-slate-400">Tage</span>
            </div>

            <div className="mt-3 space-y-2 border-t border-white/5 pt-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Münzen geben</p>
              <div className="flex items-center gap-2">
                <input
                  data-testid="grant-coins-amount"
                  type="number"
                  value={coinsAmount}
                  onChange={(e) => setCoinsAmount(parseInt(e.target.value, 10) || 0)}
                  placeholder="z.B. 500 (negativ = abziehen)"
                  className="flex-1 bg-[#0d0e15] border border-white/10 focus:border-amber-400/50 rounded-xl px-3 py-2 text-white outline-none"
                />
                <div className="flex gap-1">
                  {[100, 500, 1000].map((c) => (
                    <button
                      key={c}
                      data-testid={`grant-coins-quick-${c}`}
                      onClick={() => setCoinsAmount(c)}
                      className="px-2.5 py-2 rounded-lg text-xs font-semibold bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 transition-all"
                    >
                      +{c}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-3 space-y-2 border-t border-white/5 pt-3">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Weitere Freigaben</p>
              {[
                { key: "unlimited", label: "Unbegrenzte Nachrichten", val: permUnlimited, set: setPermUnlimited },
                { key: "adult", label: "18+ Inhalte freigeschaltet", val: permAdult, set: setPermAdult },
                { key: "admin", label: "Admin-Rechte", val: roleAdmin, set: setRoleAdmin },
              ].map((p) => (
                <button
                  key={p.key}
                  type="button"
                  data-testid={`grant-perk-${p.key}`}
                  onClick={() => p.set(!p.val)}
                  className={`w-full flex items-center justify-between rounded-xl px-4 py-2.5 border text-left transition-all ${
                    p.val ? "bg-fuchsia-500/10 border-fuchsia-400/40" : "bg-[#0d0e15] border-white/10"
                  }`}
                >
                  <span className="text-sm text-white">{p.label}</span>
                  <span className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-all ${p.val ? "bg-fuchsia-500 justify-end" : "bg-slate-600 justify-start"}`}>
                    <span className="w-4 h-4 rounded-full bg-white" />
                  </span>
                </button>
              ))}
            </div>

            <DialogFooter className="mt-2 flex-col sm:flex-row gap-2">
              <button
                data-testid="grant-revoke"
                onClick={revokeVip}
                disabled={granting}
                className="w-full sm:w-auto bg-white/5 hover:bg-rose-500/10 border border-white/10 hover:border-rose-400/40 text-slate-300 hover:text-rose-300 font-medium rounded-xl px-4 py-2.5 transition-all disabled:opacity-60"
              >
                VIP entfernen
              </button>
              <button
                data-testid="grant-confirm"
                onClick={saveGrant}
                disabled={granting}
                className="w-full sm:flex-1 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold rounded-xl px-4 py-2.5 shadow-lg shadow-violet-500/20 active:scale-95 transition-all disabled:opacity-60"
              >
                {granting ? "..." : "Freigaben speichern"}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={!!historyUser} onOpenChange={(o) => !o && setHistoryUser(null)}>
          <DialogContent className="glass border-white/10 text-slate-200" data-testid="coin-history-modal">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <History className="w-5 h-5 text-amber-300" /> Münzverlauf
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Transaktionen von <span className="text-white font-semibold">{historyUser?.name}</span>
                {" · "}Guthaben: <span className="text-amber-300 font-semibold">{(historyUser?.coins ?? 0).toLocaleString("de-DE")}</span>
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1">
              {historyLoading ? (
                <p className="text-sm text-slate-400 py-6 text-center">Lädt...</p>
              ) : historyRows.length === 0 ? (
                <p className="text-sm text-slate-500 py-6 text-center" data-testid="coin-history-empty">Noch keine Transaktionen.</p>
              ) : (
                <ul className="divide-y divide-white/5">
                  {historyRows.map((r, i) => (
                    <li key={i} data-testid="coin-history-row" className="flex items-center justify-between gap-3 py-2.5">
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{r.reason}</p>
                        <p className="text-xs text-slate-500">{fmtDate(r.created_at)}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-sm font-bold ${r.delta >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                          {r.delta >= 0 ? "+" : ""}{r.delta.toLocaleString("de-DE")}
                        </span>
                        <p className="text-[11px] text-slate-500">= {(r.balance_after ?? 0).toLocaleString("de-DE")}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!chatView} onOpenChange={(o) => !o && setChatView(null)}>
          <DialogContent className="glass border-white/10 text-slate-200 max-w-2xl" data-testid="owner-chat-transcript">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-fuchsia-400" /> Chatverlauf
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                {chatView && !chatView.loading
                  ? <><span className="text-white font-semibold">{chatView.user_name}</span> mit {chatView.character_name}</>
                  : "Lädt..."}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto space-y-3 pr-1">
              {chatViewLoading || (chatView && chatView.loading) ? (
                <p className="text-sm text-slate-400 py-6 text-center">Lädt...</p>
              ) : chatView && chatView.messages && chatView.messages.length > 0 ? (
                chatView.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                      m.role === "user"
                        ? "bg-fuchsia-500/20 text-white rounded-br-sm"
                        : "bg-white/5 text-slate-200 rounded-bl-sm"
                    }`}>
                      <p className="text-[10px] uppercase tracking-wider opacity-60 mb-0.5">
                        {m.role === "user" ? chatView.user_name : chatView.character_name}
                      </p>
                      <p className="whitespace-pre-wrap leading-relaxed">{m.content}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500 py-6 text-center">Keine Nachrichten.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>

      </div>
    </div>
  );
}
